import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, Loader, Search, X } from "lucide-react";

/**
 * InfiniteScrollSelect
 *
 * A custom dropdown that loads options incrementally as the user scrolls.
 * Includes a search filter that queries the backend as the user types.
 *
 * Props:
 *  - value           : currently selected value (id/string)
 *  - onChange        : (value) => void   called when user picks an option
 *  - placeholder     : string shown when nothing is selected
 *  - fetchOptions    : async (page, pageSize, search) => { results: [], next: bool }
 *                      Must return an object with `results` array and `next`
 *                      (boolean or next-page URL) to indicate more pages exist.
 *  - getLabel        : (item) => string  display name for each item
 *  - getValue        : (item) => string  unique key/id for each item
 *  - pageSize        : number of items per page (default 12)
 *  - required        : boolean
 *  - disabled        : boolean
 *  - className       : extra classes for the trigger button
 *  - dropdownClass   : extra classes for the dropdown panel
 *  - clearable       : boolean, shows an X on the trigger to remove the selection
 *  - clearLabel      : label for the "nothing selected" row in the list
 *                      (defaults to placeholder)
 */
const InfiniteScrollSelect = ({
    value,
    onChange,
    placeholder = "Select…",
    fetchOptions,
    getLabel,
    getValue,
    pageSize = 12,
    initialOptions = [],   // pre-seed (e.g. currently selected agent in edit mode)
    required = false,
    disabled = false,
    className = "",
    dropdownClass = "",
    clearable = false,
    clearLabel,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState(initialOptions);   // pre-seed with initialOptions
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoaded, setInitialLoaded] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const containerRef = useRef(null);
    const listRef = useRef(null);
    const isFetchingRef = useRef(false);
    const searchInputRef = useRef(null);
    const debounceTimerRef = useRef(null);

    // ------------------------------------------------------------------
    // Close when clicking outside
    // ------------------------------------------------------------------
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ------------------------------------------------------------------
    // Sync initialOptions into internal options state
    // ------------------------------------------------------------------
    useEffect(() => {
        if (initialOptions?.length > 0) {
            setOptions((prev) => {
                // Merge initialOptions into existing options, keeping uniqueness
                const merged = [...initialOptions, ...prev];
                const seen = new Set();
                return merged.filter(o => {
                    const key = String(getValue(o));
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                });
            });
        }
    }, [initialOptions, getValue]);

    // ------------------------------------------------------------------
    // Fetch a page of results
    // ------------------------------------------------------------------
    const loadPage = useCallback(
        async (pageNum, search = "") => {
            if (isFetchingRef.current) return;
            isFetchingRef.current = true;
            setLoading(true);
            try {
                const data = await fetchOptions(pageNum, pageSize, search);
                // Support both { results, next } and plain arrays
                const results = data?.results ?? (Array.isArray(data) ? data : []);
                const more = Boolean(data?.next);

                setOptions((prev) => {
                    // If page 1, we replace everything unless it's the initial pre-seed
                    // Build merged list then deduplicate (keep first occurrence)
                    const merged = pageNum === 1
                        ? (search ? results : [...initialOptions, ...results])
                        : [...prev, ...results];
                    
                    const seen = new Set();
                    return merged.filter(o => {
                        const key = String(getValue(o));
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });
                });
                setHasMore(more);
                setPage(pageNum + 1);
            } catch (err) {
                console.error("[InfiniteScrollSelect] fetchOptions error:", err);
            } finally {
                setLoading(false);
                isFetchingRef.current = false;
            }
        },
        [fetchOptions, pageSize, initialOptions, getValue]
    );

    // ------------------------------------------------------------------
    // Load first page when dropdown opens (once)
    // ------------------------------------------------------------------
    useEffect(() => {
        if (isOpen && !initialLoaded) {
            setInitialLoaded(true);
            loadPage(1, searchTerm);
        }
        // Focus search input when dropdown opens
        if (isOpen) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen, initialLoaded, loadPage, searchTerm]);

    // ------------------------------------------------------------------
    // Handle Search Term Changes
    // ------------------------------------------------------------------
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);

        // Reset and fetch from page 1 with debounce
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        
        debounceTimerRef.current = setTimeout(() => {
            setPage(1);
            setHasMore(true);
            loadPage(1, value);
        }, 400);
    };

    // ------------------------------------------------------------------
    // Scroll handler – load next page when near bottom
    // ------------------------------------------------------------------
    const handleScroll = useCallback(() => {
        const el = listRef.current;
        if (!el || loading || !hasMore) return;
        const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
        if (nearBottom) {
            loadPage(page, searchTerm);
        }
    }, [loading, hasMore, page, loadPage, searchTerm]);

    // ------------------------------------------------------------------
    // Derived – selected option label
    // ------------------------------------------------------------------
    const selectedLabel = (() => {
        const found = options.find((o) => String(getValue(o)) === String(value));
        return found ? getLabel(found) : null;
    })();

    const showClear = clearable && !disabled && value !== "" && value != null;

    // Once "No secondary agent" is picked the trigger says so, in normal text.
    // Falling back to the grey "Select Agent (Optional)" placeholder read as if
    // the pick hadn't worked (Joshua, Monday 13213284055).
    const isEmpty = value === "" || value == null;
    const emptyChoiceLabel = clearable && clearLabel && isEmpty ? clearLabel : null;

    // ------------------------------------------------------------------
    // Toggle open
    // ------------------------------------------------------------------
    const handleToggle = () => {
        if (disabled) return;
        setIsOpen((prev) => !prev);
    };

    // ------------------------------------------------------------------
    // Select an option
    // ------------------------------------------------------------------
    const handleSelect = (item) => {
        onChange(getValue(item));
        setIsOpen(false);
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {/* Hidden native input for form validation */}
            {required && (
                <input
                    tabIndex={-1}
                    required
                    value={value ?? ""}
                    onChange={() => { }}
                    style={{
                        position: "absolute",
                        opacity: 0,
                        pointerEvents: "none",
                        height: 0,
                        width: 0,
                    }}
                />
            )}

            {/* Trigger button */}
            <button
                type="button"
                onClick={handleToggle}
                disabled={disabled}
                className={`w-full bg-white border border-[#E4E6E9] rounded-[2.81px] px-4 py-3 text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#55C5D0]/20 transition-all flex items-center justify-between gap-2 text-left ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                    } ${showClear ? "pr-16" : ""} ${dropdownClass}`}
            >
                <span className={selectedLabel || emptyChoiceLabel ? "text-[#353B48]" : "text-[#9CA3AF]"}>
                    {selectedLabel ?? emptyChoiceLabel ?? placeholder}
                </span>
                <ChevronDown
                    className={`w-4 h-4 text-[#9CA3AF] flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                        }`}
                />
            </button>

            {/* Remove the current selection (sits beside the chevron, outside
                the trigger so it doesn't open the dropdown) */}
            {showClear && (
                <button
                    type="button"
                    onClick={() => { onChange(""); setIsOpen(false); }}
                    title="Remove"
                    aria-label="Remove selection"
                    className="absolute right-10 top-1/2 -translate-y-1/2 p-1 rounded text-[#9CA3AF] hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>
            )}

            {/* Dropdown panel */}
            {isOpen && (
                <div
                    className="absolute z-[200] mt-1 w-full bg-white border border-[#E4E6E9] rounded-[4px] shadow-lg overflow-hidden flex flex-col"
                    style={{ maxHeight: 300 }}
                >
                    {/* Search Input */}
                    <div className="p-2 border-b border-[#E4E6E9] sticky top-0 bg-white z-10">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                placeholder="Search agents..."
                                className="w-full pl-8 pr-8 py-1.5 text-[13px] border border-[#E4E6E9] rounded focus:outline-none focus:ring-1 focus:ring-[#55C5D0] border-gray-200"
                            />
                            {searchTerm && (
                                <button 
                                    onClick={() => {
                                        setSearchTerm("");
                                        handleSearchChange({ target: { value: "" } });
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>

                    <ul
                        ref={listRef}
                        onScroll={handleScroll}
                        className="overflow-y-auto flex-1"
                        style={{ maxHeight: 240 }}
                    >
                        {/* Empty/placeholder option (only show when not searching) */}
                        {!searchTerm && (
                            <li
                                onMouseDown={() => { onChange(""); setIsOpen(false); }}
                                className={`px-4 py-2.5 text-[13px] cursor-pointer hover:bg-[#F0FAFB] transition-colors ${!value ? "bg-[#E8F7F9] font-semibold text-[#55C5D0]" : "text-[#9CA3AF]"
                                    }`}
                            >
                                {clearLabel ?? placeholder}
                            </li>
                        )}

                        {options.map((item) => {
                            const itemVal = String(getValue(item));
                            const isSelected = String(value) === itemVal;
                            return (
                                <li
                                    key={itemVal}
                                    onMouseDown={() => handleSelect(item)}
                                    className={`px-4 py-2.5 text-[13px] cursor-pointer hover:bg-[#F0FAFB] transition-colors ${isSelected
                                        ? "bg-[#E8F7F9] font-semibold text-[#55C5D0]"
                                        : "text-[#353B48]"
                                        }`}
                                >
                                    {getLabel(item)}
                                </li>
                            );
                        })}

                        {/* Loading indicator */}
                        {loading && (
                            <li className="flex items-center justify-center gap-2 px-4 py-3 text-[12px] text-[#9CA3AF]">
                                <Loader className="w-3.5 h-3.5 animate-spin" />
                                Loading...
                            </li>
                        )}

                        {/* End of list */}
                        {!loading && !hasMore && options.length > 0 && (
                            <li className="px-4 py-2 text-center text-[11px] text-[#C4C9D3]">
                                All results loaded
                            </li>
                        )}

                        {/* No results */}
                        {!loading && options.length === 0 && (
                            <li className="px-4 py-3 text-[13px] text-[#9CA3AF] text-center">
                                No results found
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default InfiniteScrollSelect;

